/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import { Injectable }            from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';

import { User                  } from '@/models/user';
import { AuthenticationService } from "@/services/authentication.service";
import { IProjectRow           } from "@/screens/administration/i.project.row";


@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class ProjectService
 *
 * @brief CRUD access to project models
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2022
 *
 */
export class ProjectService {

  /** standard constructor */
  constructor(
    private http: HttpClient,
    private auth: AuthenticationService
  ) {


  }

  /** fetch the list of projects */
  getAll() {

    return this.http.get<IProjectRow[]>(`/backend/projects/all`);
  }

  /** find a specific project (by OID) */
  getById(id: number) {

    return this.http.get(`/backend/projects/list/${id}`);
  }

  /** fetch any headers we need to send along with our request */
  headers(): HttpHeaders {

    const user: User = this.auth.userOfSession;

    if (!user) {

      /* no user logged in */

      return null;
    }

    return new HttpHeaders().set('Authorization', user.token).set('Content-Type', 'application/json');
  }

  /** helper to just get a simple CSV style dump */
  dump(prid: string = '') {

    if (prid.length != 0) {

      return this.http.get(`/backend/report/projectdump/${prid}`);
    }

    return this.http.get(`/backend/report/projectdump`);
  }

  /** helper for userProjects() on self */
  myProjects() {

    const user: User = this.auth.userOfSession;

    if (!user) {

      /* no user logged in */

      return null;
    }

    return this.userProjects(user.username);
  }

  /** fetch the projects that this user has access to; by admin and by membership */
  userProjects(userid: string) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.get(`/backend/user/projects/${userid}`, {
      headers
    });
  }

  /** delete a project, we don't actually delete, we just mark as deleted. */
  deleteProject(prid: number|string) {

    const upd: IProjectRow = {
      is_deleted: true
    };

    return this.update(prid, upd);
  }

  /** undelete a project */
  undeleteProject(prid: number|string) {

    const upd: IProjectRow = {
      is_deleted: false
    };

    return this.update(prid, upd);
  }

  /** update a project */
  update(prid: number|string, data: IProjectRow) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.post(`/backend/projects/edit/${prid}`, data, {
      headers
    });
  }

  /** create a project */
  create(data: IProjectRow) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.post(`/backend/projects/create`, data, {
      headers
    });
  }

  /** link the project (oid) to the subdivision (sid) */
  linkSubdivision(pid: number, sid: number) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.put(`/backend/project/sub/link/${pid}/${sid}`, {
      headers
    });
  }

  /** unlink the project (oid) from the subdivision (sid) */
  unlinkSubdivision(pid: number, sid: number) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.put(`/backend/project/sub/unlink/${pid}/${sid}`, {
      headers
    });
  }

}
